'use client'

import * as React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, type UseFormSetError } from 'react-hook-form'
import type { JobApplicationInput } from '@/lib/forms/schemas'
import { jobApplicationSchema } from '@/lib/forms/schemas'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { FormStatus } from '@/components/forms/form-status'

type ApiResponse =
  | { ok: true; message: string; warning?: string; fieldErrors?: Record<string, string[]> }
  | { ok: false; message: string; fieldErrors?: Record<string, string[]> }

const roleOptions = [
  { value: '', label: 'Select a role' },
  { value: 'frontend', label: 'Frontend engineer' },
  { value: 'designer', label: 'Product designer' },
  { value: 'product', label: 'Product manager' },
  { value: 'operations', label: 'Operations' },
  { value: 'other', label: 'Other' },
]

function setServerErrors(
  fieldErrors: Record<string, string[]> | undefined,
  setError: UseFormSetError<JobApplicationInput>,
) {
  if (!fieldErrors) return

  for (const [name, messages] of Object.entries(fieldErrors)) {
    if (!messages.length) continue
    const fieldName = name as keyof JobApplicationInput
    setError(fieldName, { type: 'server', message: messages[0] })
  }
}

export function JobApplicationForm() {
  const [startedAt] = React.useState(() => new Date().toISOString())
  const [status, setStatus] = React.useState<{ kind: 'idle' | 'success' | 'error'; title?: string; description?: string }>({ kind: 'idle' })

  const form = useForm<JobApplicationInput>({
    resolver: zodResolver(jobApplicationSchema),
    defaultValues: {
      name: '',
      email: '',
      role: 'frontend',
      portfolio: '',
      linkedin: '',
      message: '',
      honeypot: '',
      startedAt,
      source: 'careers-apply',
    },
    mode: 'onBlur',
  })

  const onSubmit = form.handleSubmit(async values => {
    setStatus({ kind: 'idle' })
    form.clearErrors()

    const response = await fetch('/api/applications', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    })

    const data = (await response.json().catch(() => null)) as ApiResponse | null

    if (!response.ok || !data || !data.ok) {
      setServerErrors(data && !data.ok ? data.fieldErrors : undefined, form.setError)
      setStatus({
        kind: 'error',
        title: 'Application not sent',
        description: data?.message ?? 'Please review your details and try again.',
      })
      return
    }

    form.reset({
      name: '',
      email: '',
      role: 'frontend',
      portfolio: '',
      linkedin: '',
      message: '',
      honeypot: '',
      startedAt: new Date().toISOString(),
      source: 'careers-apply',
    })

    setStatus({
      kind: 'success',
      title: 'Application received',
      description: data.warning ?? data.message,
    })
  })

  return (
    <form className="grid gap-6" onSubmit={onSubmit}>
      <input type="hidden" {...form.register('honeypot')} aria-hidden="true" tabIndex={-1} autoComplete="off" />
      <input type="hidden" {...form.register('startedAt')} />
      <input type="hidden" {...form.register('source')} />

      <FormStatus kind={status.kind} title={status.title} description={status.description} />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="app-name">Name</Label>
          <Input id="app-name" autoComplete="name" placeholder="Your name" {...form.register('name')} />
          {form.formState.errors.name ? <p className="text-sm text-destructive">{form.formState.errors.name.message}</p> : null}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="app-email">Email</Label>
          <Input id="app-email" type="email" autoComplete="email" placeholder="you@example.com" {...form.register('email')} />
          {form.formState.errors.email ? <p className="text-sm text-destructive">{form.formState.errors.email.message}</p> : null}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="app-role">Role</Label>
          <Select id="app-role" {...form.register('role')}>
            {roleOptions.map(option => (
              <option key={option.value || 'empty'} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          {form.formState.errors.role ? <p className="text-sm text-destructive">{form.formState.errors.role.message}</p> : null}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="app-portfolio">Portfolio</Label>
          <Input
            id="app-portfolio"
            placeholder="https://"
            {...form.register('portfolio', {
              setValueAs: value => (typeof value === 'string' && value.trim().length > 0 ? value : undefined),
            })}
          />
          {form.formState.errors.portfolio ? <p className="text-sm text-destructive">{form.formState.errors.portfolio.message}</p> : null}
        </div>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="app-linkedin">LinkedIn</Label>
        <Input
          id="app-linkedin"
          placeholder="https://linkedin.com/in/..."
          {...form.register('linkedin', {
            setValueAs: value => (typeof value === 'string' && value.trim().length > 0 ? value : undefined),
          })}
        />
        {form.formState.errors.linkedin ? <p className="text-sm text-destructive">{form.formState.errors.linkedin.message}</p> : null}
      </div>

      <div className="grid gap-2">
        <Label htmlFor="app-message">Message</Label>
        <Textarea
          id="app-message"
          rows={7}
          placeholder="Tell us what kind of role you want and what you’d like to work on."
          {...form.register('message')}
        />
        {form.formState.errors.message ? <p className="text-sm text-destructive">{form.formState.errors.message.message}</p> : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button type="submit" disabled={form.formState.isSubmitting} className="sm:min-w-48">
          {form.formState.isSubmitting ? 'Submitting...' : 'Submit application'}
        </Button>
        <p className="text-sm leading-6 text-muted-foreground">We keep your details even if the alert step needs to retry.</p>
      </div>
    </form>
  )
}
